import { LitElement, html, nothing, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { MECHANIC_INFO } from '@juegazo/content';
import {
  ALL_MECHANICS,
  DEFAULT_ROOM_CONFIG,
  MAX_BASE_DURATION_MS,
  MAX_BOMBS,
  MIN_BASE_DURATION_MS,
  MIN_BOMBS,
  TIME_PRESETS_MS,
  type Mechanic,
  type RoomConfig,
} from '@juegazo/shared';

@customElement('jgz-game-config')
export class JgzGameConfig extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Object }) config: RoomConfig = DEFAULT_ROOM_CONFIG;
  @state() private mechanicSheetOpen = false;
  @state() private focusedMechanic: Mechanic | null = null;

  private overlayHost: HTMLDivElement | null = null;

  private getOverlayHost(): HTMLDivElement {
    if (!this.overlayHost) {
      this.overlayHost = document.createElement('div');
      document.body.appendChild(this.overlayHost);
    }
    return this.overlayHost;
  }

  private syncOverlay() {
    const host = this.getOverlayHost();
    render(
      this.mechanicSheetOpen
        ? html`
            <div
              class="bottom-sheet-backdrop"
              @click=${() => (this.mechanicSheetOpen = false)}
            ></div>
            ${this.renderMechanicSheet()}
          `
        : nothing,
      host,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.overlayHost?.remove();
    this.overlayHost = null;
  }

  private openSheet() {
    this.focusedMechanic = null;
    this.mechanicSheetOpen = true;
  }

  private focusMechanic = (m: Mechanic) => {
    this.focusedMechanic = m;
  };

  @state() private workingTotalBombs: number | null = null;
  @state() private workingMechanics: Mechanic[] | null = null;
  @state() private workingMixedMode: boolean | null = null;
  @state() private workingBaseDurationMs: number | null = null;

  private get totalBombs(): number {
    return this.workingTotalBombs ?? this.config?.totalBombs ?? DEFAULT_ROOM_CONFIG.totalBombs;
  }

  private get enabledMechanics(): Mechanic[] {
    return (
      this.workingMechanics ?? this.config?.enabledMechanics ?? DEFAULT_ROOM_CONFIG.enabledMechanics
    );
  }

  private get mixedMode(): boolean {
    return this.workingMixedMode ?? this.config?.mixedMode ?? DEFAULT_ROOM_CONFIG.mixedMode;
  }

  private get baseDurationMs(): number {
    return (
      this.workingBaseDurationMs ?? this.config?.baseDurationMs ?? DEFAULT_ROOM_CONFIG.baseDurationMs
    );
  }

  private emit() {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          totalBombs: this.totalBombs,
          enabledMechanics: [...this.enabledMechanics],
          mixedMode: this.mixedMode,
          baseDurationMs: this.baseDurationMs,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private arraysEqual(a: readonly Mechanic[], b: readonly Mechanic[]): boolean {
    if (a.length !== b.length) return false;
    const sb = new Set(b);
    return a.every((x) => sb.has(x));
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('config') && this.config) {
      if (this.workingTotalBombs !== null && this.config.totalBombs === this.workingTotalBombs) {
        this.workingTotalBombs = null;
      }
      if (
        this.workingMechanics !== null &&
        this.arraysEqual(this.workingMechanics, this.config.enabledMechanics)
      ) {
        this.workingMechanics = null;
      }
      if (this.workingMixedMode !== null && this.config.mixedMode === this.workingMixedMode) {
        this.workingMixedMode = null;
      }
      if (
        this.workingBaseDurationMs !== null &&
        this.config.baseDurationMs === this.workingBaseDurationMs
      ) {
        this.workingBaseDurationMs = null;
      }
    }
    this.syncOverlay();
  }

  private changeBombCount = (delta: number) => {
    const next = Math.max(MIN_BOMBS, Math.min(MAX_BOMBS, this.totalBombs + delta));
    if (next === this.totalBombs) return;
    this.workingTotalBombs = next;
    this.emit();
  };

  private setTotalBombs = (value: number) => {
    const clamped = Math.max(MIN_BOMBS, Math.min(MAX_BOMBS, Math.round(value)));
    if (clamped === this.totalBombs) return;
    this.workingTotalBombs = clamped;
    this.emit();
  };

  private changeBaseDuration = (value: number) => {
    const clamped = Math.max(MIN_BASE_DURATION_MS, Math.min(MAX_BASE_DURATION_MS, value));
    if (clamped === this.baseDurationMs) return;
    this.workingBaseDurationMs = clamped;
    this.emit();
  };

  private toggleMechanic = (mechanic: Mechanic, enabled: boolean) => {
    const set = new Set<Mechanic>(this.enabledMechanics);
    if (enabled) set.add(mechanic);
    else set.delete(mechanic);
    if (set.size === 0) return;
    this.workingMechanics = Array.from(set);
    this.emit();
  };

  private selectSingleMechanic = (mechanic: Mechanic) => {
    if (this.enabledMechanics.length === 1 && this.enabledMechanics[0] === mechanic) return;
    this.workingMechanics = [mechanic];
    this.emit();
  };

  private toggleMixed = () => {
    this.workingMixedMode = !this.mixedMode;
    this.emit();
  };

  private setMixed = (value: boolean) => {
    if (this.mixedMode === value) return;
    if (!value && this.enabledMechanics.length > 1) {
      const first = this.enabledMechanics[0];
      this.workingMixedMode = false;
      this.workingMechanics = [first];
      this.emit();
      return;
    }
    this.workingMixedMode = value;
    this.emit();
  };

  render() {
    const mechanicLabel = this.mixedMode
      ? html`Al azar entre varias
          <span
            class="muted"
            style="font-weight: 500; font-size: 0.9rem;"
            >· ${this.enabledMechanics.length} seleccionadas</span
          >`
      : html`Una mecánica
          <span
            class="muted"
            style="font-weight: 500; font-size: 0.9rem;"
            >· ${MECHANIC_INFO[this.enabledMechanics[0] ?? 'category'].name}</span
          >`;
    return html`
      <div class="stack" style="gap: 0.75rem;">
        <div>
          <span style="font-weight: 700; font-size: 0.95rem;">
            Selecciona la cantidad de bombas a jugar
          </span>
          <div
            class="preset-grid bomb-presets"
            role="radiogroup"
            aria-label="Cantidad de bombas"
          >
            ${Array.from({ length: MAX_BOMBS }, (_, i) => {
              const value = i + 1;
              const checked = this.totalBombs === value;
              return html`
                <label class="radio-preset ${checked ? 'is-selected' : ''}">
                  <input
                    type="radio"
                    name="bomb-count"
                    .value=${String(value)}
                    .checked=${checked}
                    @input=${() => this.setTotalBombs(value)}
                    @change=${(e: Event) => e.stopPropagation()}
                  />
                  <span class="radio-preset-label">${value}</span>
                </label>
              `;
            })}
          </div>
        </div>

        <div>
          <span style="font-weight: 700; font-size: 0.95rem;">
            Tiempo base (se le suma aleatoriamente hasta 15 seg extra)
          </span>
          <div
            class="preset-grid time-presets"
            role="radiogroup"
            aria-label="Tiempo base"
          >
            ${TIME_PRESETS_MS.map((ms) => {
              const checked = this.baseDurationMs === ms;
              return html`
                <label class="radio-preset ${checked ? 'is-selected' : ''}">
                  <input
                    type="radio"
                    name="time-preset"
                    .value=${String(ms)}
                    .checked=${checked}
                    @input=${() => this.changeBaseDuration(ms)}
                    @change=${(e: Event) => e.stopPropagation()}
                  />
                  <span class="radio-preset-label">${(ms / 1000).toFixed(0)}s</span>
                </label>
              `;
            })}
          </div>
        </div>

        <button
          class="mechanic-trigger"
          type="button"
          @click=${this.openSheet}
        >
          <div class="row" style="gap: 0.6rem; align-items: center; width: 100%;">
            <div class="mechanic-trigger-icon">⚙</div>
            <div style="flex: 1; text-align: left;">
              <div
                style="font-size: 0.78rem; letter-spacing: 0.18em; font-weight: 700; color: var(--muted);"
              >
                MECÁNICA
              </div>
              <div style="font-weight: 700; margin-top: 0.1rem;">${mechanicLabel}</div>
            </div>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M7 10l5 5 5-5" />
            </svg>
          </div>
        </button>
      </div>
    `;
  }

  private renderMechanicSheet() {
    return html`
      <div class="bottom-sheet" role="dialog" aria-label="Configuración de mecánica">
        <div class="bottom-sheet-handle"></div>
        <div class="bottom-sheet-header">
          <span class="bottom-sheet-title">Configurar mecánica</span>
          <button
            class="bottom-sheet-close"
            type="button"
            @click=${() => (this.mechanicSheetOpen = false)}
            aria-label="Cerrar"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="bottom-sheet-content">
          <div class="stack" style="gap: 0.4rem; margin-bottom: 0.8rem;">
            <div class="mechanic-segments" role="tablist">
              <button
                type="button"
                role="tab"
                class="mechanic-segment ${this.mixedMode ? '' : 'active'}"
                @click=${() => this.setMixed(false)}
              >
                <span class="segment-label">Una mecánica</span>
                <span class="segment-sublabel">Siempre la misma</span>
              </button>
              <button
                type="button"
                role="tab"
                class="mechanic-segment ${this.mixedMode ? 'active' : ''}"
                @click=${() => this.setMixed(true)}
              >
                <span class="segment-label">Al azar entre varias</span>
                <span class="segment-sublabel">Se elige una al azar</span>
              </button>
            </div>
          </div>
          <div class="stack" style="gap: 0.4rem;">
            <div class="row" style="justify-content: space-between; align-items: baseline;">
              <span class="muted" style="font-size: 1rem;"
                >${this.mixedMode
                  ? 'Elige las que pueden aparecer'
                  : 'Elige la mecánica de la partida'}</span
              >
              <span
                class="muted"
                style="font-size: 1rem; font-variant-numeric: tabular-nums;"
              >
                ${this.mixedMode
                  ? html`${this.enabledMechanics.length} seleccionada${this.enabledMechanics.length === 1
                      ? ''
                      : 's'}`
                  : html`${this.enabledMechanics.length}/1`}
              </span>
            </div>
            <div class="mechanic-list mechanic-list-grid">
              ${ALL_MECHANICS.map((m) => {
                const checked = this.enabledMechanics.includes(m);
                return this.mixedMode
                  ? html`
                      <label class="mechanic-option">
                        <input
                          type="checkbox"
                          .checked=${checked}
                          @change=${(e: Event) => {
                            this.focusMechanic(m);
                            this.toggleMechanic(
                              m,
                              (e.target as HTMLInputElement).checked,
                            );
                          }}
                        />
                        <span class="mechanic-check" aria-hidden="true"></span>
                        <span class="mechanic-label">${MECHANIC_INFO[m].name}</span>
                      </label>
                    `
                  : html`
                      <label class="mechanic-option">
                        <input
                          type="radio"
                          name="mechanic-single"
                          .checked=${checked}
                          @change=${() => {
                            this.focusMechanic(m);
                            this.selectSingleMechanic(m);
                          }}
                        />
                        <span class="mechanic-radio" aria-hidden="true"></span>
                        <span class="mechanic-label">${MECHANIC_INFO[m].name}</span>
                      </label>
                    `;
              })}
            </div>
            ${this.renderMechanicExplanation()}
          </div>
        </div>
        <div class="bottom-sheet-footer">
          <button
            class="btn-pill primary"
            type="button"
            style="width: 100%;"
            @click=${() => (this.mechanicSheetOpen = false)}
          >
            <span class="label">LISTO</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderMechanicExplanation() {
    if (!this.focusedMechanic) {
      return html`
        <div class="mechanic-explanation mechanic-explanation-placeholder" aria-live="polite">
          <div class="mechanic-explanation-placeholder-text">
            Toca una mecánica y te explico qué pide.
          </div>
        </div>
      `;
    }
    const info = MECHANIC_INFO[this.focusedMechanic];
    return html`
      <div class="mechanic-explanation" aria-live="polite">
        <div class="mechanic-explanation-title">${info.name}</div>
        <div class="mechanic-explanation-desc">${info.description}</div>
        <div class="mechanic-explanation-example">${info.example}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-game-config': JgzGameConfig;
  }
}
